import { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { motion } from "framer-motion";
import { X, Save, Plus, User, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import BodyDiagram, { REGION_LABEL } from "./BodyDiagram";

function TagInput({ label, values, onChange, placeholder, testId, danger }) {
  const [input, setInput] = useState("");
  const add = () => {
    const v = input.trim();
    if (!v) return;
    if (!values.includes(v)) onChange([...values, v]);
    setInput("");
  };
  const remove = (v) => onChange(values.filter((x) => x !== v));
  return (
    <div>
      <label className="text-xs uppercase tracking-[0.2em] text-kawaii-textSoft font-semibold">{label}</label>
      <div className="mt-2 flex flex-wrap gap-1.5 mb-2" data-testid={`${testId}-list`}>
        {values.map((v) => (
          <span key={v} className={`inline-flex items-center gap-1.5 pl-3 pr-1 py-1 rounded-full text-xs font-body ${
            danger ? 'bg-[#FFE5E5] text-[#E63946] border border-[#E63946]/40' : 'bg-kawaii-mint/40 text-kawaii-text border border-kawaii-mint'
          }`}>
            {v}
            <button type="button" onClick={() => remove(v)} className="w-4 h-4 rounded-full hover:bg-white/60 flex items-center justify-center">
              <X className="w-2.5 h-2.5" strokeWidth={2.5} />
            </button>
          </span>
        ))}
        {values.length === 0 && <span className="text-[11px] text-kawaii-textSoft italic font-body">None yet</span>}
      </div>
      <div className="flex gap-2">
        <input
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); add(); } }}
          placeholder={placeholder}
          data-testid={`${testId}-input`}
          className="flex-1 px-3 py-2 rounded-2xl border border-kawaii-border bg-white font-body text-sm text-kawaii-text focus:outline-none focus:ring-2 focus:ring-kawaii-pink"
        />
        <button
          type="button" onClick={add} data-testid={`${testId}-add`}
          className="kawaii-btn px-3 rounded-2xl bg-kawaii-pink text-white font-body font-semibold text-xs inline-flex items-center gap-1"
        >
          <Plus className="w-3.5 h-3.5" strokeWidth={2.5} /> Add
        </button>
      </div>
    </div>
  );
}

const empty = {
  name: "", dob: "", blood_type: "",
  allergies: [], regular_medications: [], conditions: [],
  gp_name: "", gp_phone: "", gp_email: "",
  clinic_name: "", clinic_phone: "",
  emergency_contact_name: "", emergency_contact_phone: "",
  cellulitis_count_lifetime: null, hospital_admissions_lifetime: null,
  lymphedema_type: "", lymphedema_cause: "", lymphedema_areas: [], diagnosed_date: "",
  notes: "",
};

export default function ProfileModal({ onClose, onSaved }) {
  const [profile, setProfile] = useState(empty);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/profile")
      .then((r) => setProfile({ ...empty, ...r.data }))
      .catch(() => toast.error("Couldn't load profile"))
      .finally(() => setLoading(false));
  }, []);

  const update = (patch) => setProfile((p) => ({ ...p, ...patch }));

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const { data } = await api.put("/profile", profile);
      toast.success("Profile saved ♡");
      onSaved && onSaved(data);
      onClose();
    } catch { toast.error("Couldn't save"); }
    finally { setSaving(false); }
  };

  return createPortal(
    <motion.div
      className="fixed inset-0 z-50 backdrop-blur-xl bg-white/40 overflow-y-auto"
      initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
      onClick={onClose}
    >
      <motion.form
        onSubmit={submit} onClick={(e) => e.stopPropagation()}
        initial={{ y: 30, opacity: 0 }} animate={{ y: 0, opacity: 1 }}
        className="max-w-2xl mx-auto my-6 md:my-10 kawaii-card p-6 md:p-8"
        data-testid="profile-modal"
      >
        <div className="flex items-start justify-between mb-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-2xl bg-gradient-to-br from-kawaii-lavender to-kawaii-pink flex items-center justify-center">
              <User className="w-5 h-5 text-white" strokeWidth={2.5} />
            </div>
            <div>
              <p className="text-xs uppercase tracking-[0.25em] text-kawaii-textSoft font-semibold">Your medical passport</p>
              <h2 className="font-heading text-2xl font-semibold text-kawaii-text">Profile</h2>
              <p className="text-xs text-kawaii-textSoft font-body mt-1">Auto-populates your emergency script & doctor PDF.</p>
            </div>
          </div>
          <button
            type="button" onClick={onClose}
            data-testid="close-profile-btn"
            className="kawaii-btn w-10 h-10 rounded-full bg-white border border-kawaii-border flex items-center justify-center"
          >
            <X className="w-5 h-5" strokeWidth={2.5} />
          </button>
        </div>

        {loading ? (
          <div className="py-8 flex items-center justify-center gap-2 text-kawaii-textSoft">
            <Loader2 className="w-4 h-4 animate-spin" /> Loading...
          </div>
        ) : (
          <>
            <div className="grid md:grid-cols-2 gap-4 mb-5">
              <div>
                <label className="text-xs uppercase tracking-[0.2em] text-kawaii-textSoft font-semibold">Name</label>
                <input
                  value={profile.name || ""} onChange={(e) => update({ name: e.target.value })}
                  data-testid="profile-name"
                  className="mt-2 w-full px-3 py-2 rounded-2xl border border-kawaii-border bg-white font-body text-sm text-kawaii-text focus:outline-none focus:ring-2 focus:ring-kawaii-pink"
                />
              </div>
              <div>
                <label className="text-xs uppercase tracking-[0.2em] text-kawaii-textSoft font-semibold">Date of birth</label>
                <input
                  type="date" value={profile.dob || ""} onChange={(e) => update({ dob: e.target.value })}
                  data-testid="profile-dob"
                  className="mt-2 w-full px-3 py-2 rounded-2xl border border-kawaii-border bg-white font-body text-sm text-kawaii-text focus:outline-none focus:ring-2 focus:ring-kawaii-pink"
                />
              </div>
              <div>
                <label className="text-xs uppercase tracking-[0.2em] text-kawaii-textSoft font-semibold">Blood type</label>
                <select
                  value={profile.blood_type || ""} onChange={(e) => update({ blood_type: e.target.value })}
                  data-testid="profile-blood-type"
                  className="mt-2 w-full px-3 py-2 rounded-2xl border border-kawaii-border bg-white font-body text-sm text-kawaii-text focus:outline-none focus:ring-2 focus:ring-kawaii-pink"
                >
                  <option value="">— unknown —</option>
                  {["A+","A-","B+","B-","AB+","AB-","O+","O-"].map((b) => <option key={b}>{b}</option>)}
                </select>
              </div>
            </div>

            <div className="rounded-2xl border border-kawaii-lavender/40 bg-kawaii-lavender/10 p-4 mb-5">
              <div className="text-[10px] uppercase tracking-[0.25em] text-kawaii-textSoft font-bold mb-3">Lymphedema profile</div>
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <label className="text-xs uppercase tracking-[0.2em] text-kawaii-textSoft font-semibold">Type</label>
                  <div className="mt-2 flex gap-2">
                    {[
                      { v: "primary",   l: "Primary",   d: "Born with it" },
                      { v: "secondary", l: "Secondary", d: "Trauma / surgery / cancer / other" },
                    ].map((o) => (
                      <button
                        type="button" key={o.v}
                        onClick={() => update({ lymphedema_type: profile.lymphedema_type === o.v ? "" : o.v })}
                        data-testid={`profile-type-${o.v}`}
                        className={`kawaii-btn flex-1 rounded-2xl px-3 py-2 border text-left ${
                          profile.lymphedema_type === o.v ? 'bg-kawaii-lavender/60 border-kawaii-lavender' : 'bg-white border-kawaii-border'
                        }`}
                      >
                        <div className="text-sm font-heading font-semibold text-kawaii-text">{o.l}</div>
                        <div className="text-[10px] text-kawaii-textSoft font-body">{o.d}</div>
                      </button>
                    ))}
                  </div>
                </div>
                <div>
                  <label className="text-xs uppercase tracking-[0.2em] text-kawaii-textSoft font-semibold">Diagnosed (month / year)</label>
                  <input
                    type="month" value={profile.diagnosed_date || ""} onChange={(e) => update({ diagnosed_date: e.target.value })}
                    data-testid="profile-diagnosed"
                    className="mt-2 w-full px-3 py-2 rounded-2xl border border-kawaii-border bg-white font-body text-sm text-kawaii-text focus:outline-none focus:ring-2 focus:ring-kawaii-lavender"
                  />
                </div>
                {profile.lymphedema_type === "secondary" && (
                  <div className="md:col-span-2">
                    <label className="text-xs uppercase tracking-[0.2em] text-kawaii-textSoft font-semibold">Cause / trigger</label>
                    <input
                      value={profile.lymphedema_cause || ""} onChange={(e) => update({ lymphedema_cause: e.target.value })}
                      placeholder="e.g. Breast cancer treatment 2019, road traffic accident, filariasis"
                      data-testid="profile-cause"
                      className="mt-2 w-full px-3 py-2 rounded-2xl border border-kawaii-border bg-white font-body text-sm text-kawaii-text focus:outline-none focus:ring-2 focus:ring-kawaii-lavender"
                    />
                  </div>
                )}
                <div className="md:col-span-2">
                  <label className="text-xs uppercase tracking-[0.2em] text-kawaii-textSoft font-semibold">Location of edema — tap the body</label>
                  <div className="mt-2 flex flex-col md:flex-row gap-4 items-center">
                    <BodyDiagram
                      selected={profile.lymphedema_areas || []}
                      onToggle={(k) => update({
                        lymphedema_areas: (profile.lymphedema_areas || []).includes(k)
                          ? profile.lymphedema_areas.filter((x) => x !== k)
                          : [...(profile.lymphedema_areas || []), k]
                      })}
                      size={180}
                    />
                    <div className="flex-1 min-w-0">
                      <div className="flex flex-wrap gap-1.5" data-testid="profile-areas-list">
                        {(profile.lymphedema_areas || []).length === 0 ? (
                          <span className="text-xs text-kawaii-textSoft italic font-body">Tap any region to mark where your lymphedema is — head to toe.</span>
                        ) : (
                          (profile.lymphedema_areas || []).map((k) => (
                            <span key={k} className="text-[11px] font-body font-semibold uppercase tracking-wider px-2 py-0.5 rounded-full bg-kawaii-lavender/40 text-kawaii-text">
                              {REGION_LABEL[k] || k}
                            </span>
                          ))
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className="space-y-5 mb-5">
              <TagInput
                label="⚠ Allergies"
                values={profile.allergies || []}
                onChange={(v) => update({ allergies: v })}
                placeholder="e.g. Penicillin, latex"
                testId="profile-allergies" danger
              />
              <TagInput
                label="Regular medications"
                values={profile.regular_medications || []}
                onChange={(v) => update({ regular_medications: v })}
                placeholder="e.g. Prophylactic penicillin V 250mg BD"
                testId="profile-meds"
              />
              <TagInput
                label="Conditions"
                values={profile.conditions || []}
                onChange={(v) => update({ conditions: v })}
                placeholder="e.g. Chronic Lymphedema"
                testId="profile-conditions"
              />
            </div>

            <div className="grid md:grid-cols-2 gap-3 mb-4">
              <div>
                <label className="text-xs uppercase tracking-[0.2em] text-kawaii-textSoft font-semibold">GP name</label>
                <input
                  value={profile.gp_name || ""} onChange={(e) => update({ gp_name: e.target.value })}
                  data-testid="profile-gp-name"
                  className="mt-2 w-full px-3 py-2 rounded-2xl border border-kawaii-border bg-white font-body text-sm text-kawaii-text focus:outline-none focus:ring-2 focus:ring-kawaii-pink"
                />
              </div>
              <div>
                <label className="text-xs uppercase tracking-[0.2em] text-kawaii-textSoft font-semibold">GP phone</label>
                <input
                  value={profile.gp_phone || ""} onChange={(e) => update({ gp_phone: e.target.value })}
                  data-testid="profile-gp-phone"
                  className="mt-2 w-full px-3 py-2 rounded-2xl border border-kawaii-border bg-white font-body text-sm text-kawaii-text focus:outline-none focus:ring-2 focus:ring-kawaii-pink"
                />
              </div>
              <div className="md:col-span-2">
                <label className="text-xs uppercase tracking-[0.2em] text-kawaii-textSoft font-semibold">GP email <span className="text-kawaii-textSoft/60 normal-case">(for one-tap PDF share)</span></label>
                <input
                  type="email" value={profile.gp_email || ""} onChange={(e) => update({ gp_email: e.target.value })}
                  data-testid="profile-gp-email"
                  placeholder="e.g. reception@yourgp.nhs.uk"
                  className="mt-2 w-full px-3 py-2 rounded-2xl border border-kawaii-border bg-white font-body text-sm text-kawaii-text focus:outline-none focus:ring-2 focus:ring-kawaii-pink"
                />
              </div>
              <div>
                <label className="text-xs uppercase tracking-[0.2em] text-kawaii-textSoft font-semibold">Lymphedema clinic</label>
                <input
                  value={profile.clinic_name || ""} onChange={(e) => update({ clinic_name: e.target.value })}
                  data-testid="profile-clinic-name"
                  className="mt-2 w-full px-3 py-2 rounded-2xl border border-kawaii-border bg-white font-body text-sm text-kawaii-text focus:outline-none focus:ring-2 focus:ring-kawaii-pink"
                />
              </div>
              <div>
                <label className="text-xs uppercase tracking-[0.2em] text-kawaii-textSoft font-semibold">Clinic phone</label>
                <input
                  value={profile.clinic_phone || ""} onChange={(e) => update({ clinic_phone: e.target.value })}
                  data-testid="profile-clinic-phone"
                  className="mt-2 w-full px-3 py-2 rounded-2xl border border-kawaii-border bg-white font-body text-sm text-kawaii-text focus:outline-none focus:ring-2 focus:ring-kawaii-pink"
                />
              </div>
              <div>
                <label className="text-xs uppercase tracking-[0.2em] text-kawaii-textSoft font-semibold">Emergency contact name</label>
                <input
                  value={profile.emergency_contact_name || ""} onChange={(e) => update({ emergency_contact_name: e.target.value })}
                  data-testid="profile-ec-name"
                  className="mt-2 w-full px-3 py-2 rounded-2xl border border-kawaii-border bg-white font-body text-sm text-kawaii-text focus:outline-none focus:ring-2 focus:ring-kawaii-pink"
                />
              </div>
              <div>
                <label className="text-xs uppercase tracking-[0.2em] text-kawaii-textSoft font-semibold">Emergency contact phone</label>
                <input
                  value={profile.emergency_contact_phone || ""} onChange={(e) => update({ emergency_contact_phone: e.target.value })}
                  data-testid="profile-ec-phone"
                  className="mt-2 w-full px-3 py-2 rounded-2xl border border-kawaii-border bg-white font-body text-sm text-kawaii-text focus:outline-none focus:ring-2 focus:ring-kawaii-pink"
                />
              </div>
            </div>

            <div className="mt-4 grid md:grid-cols-2 gap-3">
              <div>
                <label className="text-xs uppercase tracking-[0.2em] text-kawaii-textSoft font-semibold">
                  Cellulitis episodes in your lifetime
                </label>
                <input
                  type="number" min="0" max="500"
                  value={profile.cellulitis_count_lifetime ?? ""}
                  onChange={(e) => update({ cellulitis_count_lifetime: e.target.value === "" ? null : parseInt(e.target.value, 10) })}
                  data-testid="profile-cellulitis-count"
                  placeholder="e.g. 7"
                  className="mt-2 w-full px-3 py-2 rounded-2xl border border-kawaii-border bg-white font-body text-sm text-kawaii-text focus:outline-none focus:ring-2 focus:ring-kawaii-pink"
                />
                <p className="text-[11px] text-kawaii-textSoft mt-1.5 font-body italic">
                  Included in your emergency script so triage teams see your infection history.
                </p>
              </div>
            </div>

            <div className="flex justify-end gap-2 pt-5 mt-2 border-t border-kawaii-border">
              <button
                type="button" onClick={onClose}
                className="kawaii-btn px-4 py-2 rounded-full bg-white border border-kawaii-border font-body font-semibold text-sm text-kawaii-text"
              >Cancel</button>
              <button
                type="submit" disabled={saving}
                data-testid="save-profile-btn"
                className="kawaii-btn px-5 py-2 rounded-full bg-gradient-to-r from-kawaii-pink to-kawaii-peach text-white font-body font-semibold text-sm shadow-sm inline-flex items-center gap-2 disabled:opacity-60"
              >
                {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" strokeWidth={2.5} />}
                Save profile
              </button>
            </div>
          </>
        )}
      </motion.form>
    </motion.div>,
    document.body
  );
}
